import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { api } from "../lib/api";
import { Card } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import { AlertTriangle, ClipboardList, BatteryFull, Sparkles, Check, AlertOctagon, ShieldAlert, ShieldCheck } from "lucide-react";
import { boatImage } from "../lib/boatImages";
import WelcomeBanner from "../components/WelcomeBanner";
import SeasonModeCard from "../components/SeasonModeCard";

// Map backend readiness → traffic-light styling. Used across boat overview
// surfaces so the badge always looks identical.
export function readinessChip(readiness) {
  if (readiness === "grounded") {
    return {
      label: "Sperret",
      cls: "bg-red-600 text-white",
      icon: AlertOctagon,
    };
  }
  if (readiness === "warning") {
    return {
      label: "Advarsel",
      cls: "bg-[#BD984D] text-white",
      icon: ShieldAlert,
    };
  }
  return { label: "Klar", cls: "bg-emerald-600 text-white", icon: ShieldCheck };
}

export function BoatStatusCard({ boat, linkTo, boatDefects = [], defectsBase = "/admin" }) {
  const navigate = useNavigate();
  // Batteri-policy: vises bare hvis status ikke er "ok" (kritisk eller ukjent).
  // En båt som har riktig nivå for sin modus (50 % storage, 100 % active) er
  // grønn og trenger ikke ta plass på dashbordet.
  const batteryStatus = boat.battery_status || "unknown";
  const batteryTarget = boat.battery_target_pct ?? 100;
  const storageMode = !!boat.storage_mode;
  const showBattery = boat.battery_pct != null && batteryStatus !== "ok";
  const batteryCritical = batteryStatus === "critical";
  const cleaningPending = boat.cleaning_pending ?? 0;
  const cleaningTotal = boat.cleaning_total ?? 9;
  const cleaningDone = cleaningPending === 0;
  const readiness = boat.readiness || "ready";
  const sev = boat.defect_severity || { red: 0, yellow: 0, green: 0 };
  const chip = readinessChip(readiness);
  const ChipIcon = chip.icon;

  // Clicking the "Avvik"-stat on the card jumps straight to the defect(s) on this boat.
  // 1 open defect → open it directly (focus modal).
  // 2+ open defects → land on the Avvik-page pre-filtered to this boat.
  const onAvvikClick = (e) => {
    if ((boat.open_defects ?? 0) === 0) return; // no defects → let the card link handle the click
    e.preventDefault();
    e.stopPropagation();
    if (boatDefects.length === 1) {
      navigate(`${defectsBase}/defects?focus=${boatDefects[0].id}`);
    } else {
      navigate(`${defectsBase}/defects?boat=${boat.id}`);
    }
  };

  // Clicking the readiness chip jumps to the defect responsible for the
  // warning/grounded status. Picks the most-severe open defect (red over
  // yellow). Faller tilbake til båtdetaljene når advarselen skyldes renhold
  // (siden det ikke er noe avvik å åpne).
  const readinessReason = boat.readiness_reason || [];
  const hasDefectReason = readinessReason.some(
    (r) => r === "red_defect" || r === "yellow_defect",
  );
  const cleaningOnly = !hasDefectReason
    && readinessReason.includes("cleaning_incomplete");
  const reasonLabel = readiness === "ready" ? ""
    : cleaningOnly ? "Renhold ikke fullført"
    : readinessReason.includes("red_defect") ? "Kritisk avvik"
    : readinessReason.includes("yellow_defect") ? "Åpent avvik"
    : "Klargjøring pågår";
  const onReadinessClick = (e) => {
    if (readiness === "ready") return; // green chip is just informational
    e.preventDefault();
    e.stopPropagation();
    if (cleaningOnly) {
      navigate(`${defectsBase}/boats/${boat.id}`);
      return;
    }
    const critical = boatDefects.filter((d) => d.severity === "red");
    const warning = boatDefects.filter((d) => d.severity === "yellow");
    const pool = critical.length > 0 ? critical : warning;
    if (pool.length === 1) {
      navigate(`${defectsBase}/defects?focus=${pool[0].id}`);
    } else {
      navigate(`${defectsBase}/defects?boat=${boat.id}`);
    }
  };

  const cardInner = (
    <Card
      data-testid={`boat-card-${boat.id}`}
      className="overflow-hidden hover:-translate-y-1 hover:shadow-[0_8px_30px_rgb(12,24,36,0.08)] transition-all border border-[#E2E8F0] bg-white"
    >
      <div className="relative h-40 bg-slate-100">
        <img
          src={boatImage(boat)}
          alt={boat.name}
          className="absolute inset-0 w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-[#0C1824]/55 via-transparent to-transparent" />
        <Badge
          className={`absolute top-3 right-3 ${
            boat.status === "ute"
              ? "bg-[#BD984D]/95 text-[#0C1824] hover:bg-[#BD984D]/95"
              : "bg-[#1DA3B3]/95 text-white hover:bg-[#1DA3B3]/95"
          }`}
          data-testid={`boat-status-${boat.id}`}
        >
          {boat.status === "ute" ? "Ute" : "Inne"}
        </Badge>
        <Badge
          onClick={onReadinessClick}
          role={readiness !== "ready" ? "button" : undefined}
          tabIndex={readiness !== "ready" ? 0 : undefined}
          onKeyDown={(e) => {
            if (readiness !== "ready" && (e.key === "Enter" || e.key === " ")) {
              onReadinessClick(e);
            }
          }}
          className={`absolute top-3 left-3 ${chip.cls} hover:${chip.cls} ${
            readiness !== "ready" ? "cursor-pointer hover:brightness-110 transition" : ""
          }`}
          title={readiness !== "ready" ? `${chip.label} – ${reasonLabel}. Klikk for detaljer.` : undefined}
          data-testid={`boat-readiness-${boat.id}`}
        >
          <ChipIcon className="w-3 h-3 mr-1" /> {chip.label}
          {readiness !== "ready" && (
            <span className="ml-1.5 text-[10px] opacity-80 font-normal">· {reasonLabel}</span>
          )}
        </Badge>
        <div className="absolute bottom-3 left-4 text-white">
          <div className="text-[10px] uppercase tracking-[0.32em] text-[#ECC974]">
            {boat.model} · {boat.length_ft}&apos;
          </div>
          <div className="text-xl font-bold leading-tight drop-shadow">{boat.name}</div>
        </div>
      </div>

      <div className="p-5">
        <div className={`grid ${showBattery ? "grid-cols-4" : "grid-cols-3"} gap-3 text-sm`}>
          {showBattery && (
            <div data-testid="boat-battery-warn">
              <div className="text-[10px] uppercase tracking-[0.18em] text-[#A57F39] flex items-center gap-1">
                <BatteryFull className="w-3 h-3" />
                {storageMode ? "Batteri · Storage" : "Batteri"}
              </div>
              <div className={`text-base font-semibold mt-0.5 ${batteryCritical ? "text-red-600" : "text-[#A57F39]"}`}>
                {boat.battery_pct}%
                <span className="ml-1 text-[10px] font-normal text-[#2B3F4D]">
                  mål: {batteryTarget}%
                </span>
              </div>
            </div>
          )}
          <div>
            <div className="text-[10px] uppercase tracking-[0.18em] text-[#A57F39] flex items-center gap-1">
              <Sparkles className="w-3 h-3" /> Renhold
            </div>
            {cleaningDone ? (
              <div className="text-base font-semibold mt-0.5 text-[#1DA3B3] flex items-center gap-1">
                <Check className="w-4 h-4" /> Ferdig
              </div>
            ) : (
              <div className={`text-base font-semibold mt-0.5 ${cleaningPending >= cleaningTotal ? "text-red-600" : "text-[#A57F39]"}`}>
                {cleaningPending}/{cleaningTotal}
                <span className="ml-1 text-[10px] font-normal text-[#2B3F4D]">gjenstår</span>
              </div>
            )}
          </div>
          <div
            onClick={onAvvikClick}
            role={(boat.open_defects ?? 0) > 0 ? "button" : undefined}
            tabIndex={(boat.open_defects ?? 0) > 0 ? 0 : undefined}
            onKeyDown={(e) => {
              if ((boat.open_defects ?? 0) > 0 && (e.key === "Enter" || e.key === " ")) {
                e.preventDefault();
                onAvvikClick(e);
              }
            }}
            className={(boat.open_defects ?? 0) > 0
              ? "rounded-md -mx-1 px-1 py-0.5 cursor-pointer hover:bg-[#A57F39]/10 transition-colors"
              : ""}
            data-testid={`boat-avvik-link-${boat.id}`}
            title={(boat.open_defects ?? 0) > 0 ? "Åpne avvik for denne båten" : undefined}
          >
            <div className="text-[10px] uppercase tracking-[0.18em] text-[#A57F39] flex items-center gap-1">
              <AlertTriangle className="w-3 h-3" /> Avvik
            </div>
            <div
              className={`text-base font-semibold mt-0.5 ${
                sev.red > 0
                  ? "text-red-600"
                  : sev.yellow > 0
                  ? "text-[#A57F39]"
                  : boat.open_defects > 0
                  ? "text-emerald-700"
                  : "text-[#0C1824]"
              }`}
              data-testid={`boat-defect-counts-${boat.id}`}
            >
              {boat.open_defects ?? 0}
              {(sev.red > 0 || sev.yellow > 0) && (
                <span className="ml-1 text-[10px] font-mono font-normal">
                  {sev.red > 0 && <span className="text-red-600">●{sev.red}</span>}
                  {sev.yellow > 0 && <span className="text-[#A57F39] ml-0.5">●{sev.yellow}</span>}
                </span>
              )}
            </div>
          </div>
          <div>
            <div className="text-[10px] uppercase tracking-[0.18em] text-[#A57F39] flex items-center gap-1">
              <ClipboardList className="w-3 h-3" /> Booking
            </div>
            <div className="text-base font-semibold mt-0.5 text-[#0C1824]">
              {boat.active_booking ? "Aktiv" : "—"}
            </div>
          </div>
        </div>
      </div>
    </Card>
  );

  return linkTo ? <Link to={linkTo}>{cardInner}</Link> : cardInner;
}

export default function FleetDashboard({ basePath = "/admin" }) {
  const [boats, setBoats] = useState([]);
  const [defects, setDefects] = useState([]);
  const [checklists, setChecklists] = useState([]);

  useEffect(() => {
    api.get("/boats").then((r) => setBoats(r.data)).catch(() => {});
    api.get("/defects", { params: { status: "open" } }).then((r) => setDefects(r.data)).catch(() => {});
    api.get("/checklists", { params: { status: "pending" } }).then((r) => setChecklists(r.data)).catch(() => {});
  }, []);

  const cleaningPendingBoats = boats.filter((b) => (b.cleaning_pending ?? 0) > 0).length;
  const stats = [
    { label: "Båter ute", value: boats.filter((b) => b.status === "ute").length },
    { label: "Trenger renhold", value: cleaningPendingBoats, accent: cleaningPendingBoats > 0, accentClass: "text-[#A57F39]", linkTo: `${basePath}/vaskeplan` },
    { label: "Åpne avvik", value: defects.length, accent: defects.length > 0, linkTo: defects.length > 0 ? `${basePath}/defects` : null },
    { label: "Pågående sjekklister", value: checklists.length, linkTo: `${basePath}/checklists` },
  ];

  // Group defects by boat so each card knows its own list.
  const defectsByBoat = defects.reduce((acc, d) => {
    (acc[d.boat_id] = acc[d.boat_id] || []).push(d);
    return acc;
  }, {});

  return (
    <div className="space-y-8">
      <WelcomeBanner />
      <SeasonModeCard />
      <div>
        <div className="text-xs uppercase tracking-[0.32em] text-[#A57F39]">Flåteoversikt</div>
        <h1 className="text-3xl sm:text-4xl font-bold text-[#0C1824] mt-1">Dashboard</h1>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        {stats.map((s) => {
          const card = (
            <Card
              key={s.label}
              className={`p-5 border border-[#E2E8F0] bg-white ${
                s.linkTo ? "transition-shadow hover:shadow-md cursor-pointer" : ""
              }`}
              data-testid={`stat-${s.label.replaceAll(" ", "-").toLowerCase()}`}
            >
              <div className="text-xs uppercase tracking-[0.18em] text-[#A57F39]">{s.label}</div>
              <div
                className={`text-3xl font-bold mt-2 ${
                  s.accent ? (s.accentClass || "text-red-600") : "text-[#0C1824]"
                }`}
              >
                {s.value}
              </div>
            </Card>
          );
          return s.linkTo ? (
            <Link key={s.label} to={s.linkTo}>
              {card}
            </Link>
          ) : (
            card
          );
        })}
      </div>

      <div>
        <h2 className="text-xl font-semibold text-[#0C1824] mb-4">Båter</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
          {boats.map((b) => (
            <BoatStatusCard
              key={b.id}
              boat={b}
              linkTo={`${basePath}/boats/${b.id}`}
              boatDefects={defectsByBoat[b.id] || []}
              defectsBase={basePath}
            />
          ))}
        </div>
      </div>
    </div>
  );
}
